import { useEffect, useRef, useState } from 'react';

/**
 * CareerTimeline — compact "The Arc" rail at the top of an athlete's
 * Network profile. Premium, smooth, slim. Each node is small (year stays
 * legible, label drops to ~10px italic). When a milestone has an
 * `image` (team helmet / school logo URL set in admin), it replaces
 * the dot with a small circular logo — auto-pulled.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveLogo = (src) =>
  src && typeof src === 'string' && src.startsWith('/api/') ? `${BACKEND_URL}${src}` : src;

const CareerTimeline = ({ milestones = [] }) => {
  const ref = useRef(null);
  const btnRefs = useRef({});
  const [revealedCount, setRevealedCount] = useState(0);
  // Which milestone's stats popover is open (null = none). Tap the dot
  // to open, tap again (or elsewhere / Esc) to close.
  const [openIdx, setOpenIdx] = useState(null);
  // Cached bounding rect of the currently-open trigger button so we can
  // position the popover as `position: fixed` — this escapes the parent
  // scroll container's clipping and avoids z-index fights with other
  // sections on the page.
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !('IntersectionObserver' in window)) {
      setRevealedCount(milestones.length);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          milestones.forEach((_, i) => {
            setTimeout(() => setRevealedCount((c) => Math.max(c, i + 1)), 80 + i * 110);
          });
          observer.disconnect();
        }
      });
    }, { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [milestones]);

  // Close popover on outside click or Escape.
  useEffect(() => {
    if (openIdx === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setOpenIdx(null); setPopoverAnchor(null); } };
    const onClick = (e) => {
      // Any click NOT inside the timeline root closes the popover. Clicks
      // inside are handled by the milestone button (toggle) directly.
      if (ref.current && !ref.current.contains(e.target)) { setOpenIdx(null); setPopoverAnchor(null); }
    };
    // When the page scrolls, close the popover immediately — feels
    // snappier than trying to keep it glued to the anchor while the
    // athlete's page moves under it. Resize just closes too (rare, and
    // simpler than recomputing).
    const closeOnMove = () => { setOpenIdx(null); setPopoverAnchor(null); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    window.addEventListener('touchstart', onClick, { passive: true });
    window.addEventListener('scroll', closeOnMove, { passive: true, capture: true });
    window.addEventListener('resize', closeOnMove);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('touchstart', onClick);
      window.removeEventListener('scroll', closeOnMove, { capture: true });
      window.removeEventListener('resize', closeOnMove);
    };
  }, [openIdx]);

  if (!milestones.length) return null;
  const fillPct = Math.min(100, Math.round((revealedCount / milestones.length) * 100));

  return (
    <section
      ref={ref}
      className="relative"
      aria-label="Career timeline"
      data-testid="career-timeline"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="h-px w-5 bg-blue-300/40" />
        <p className="font-display text-[9px] uppercase tracking-[0.28em] text-blue-300/85">The Arc · swipe →</p>
      </div>

      <div className="relative">
        <div
          className="overflow-x-auto no-scrollbar pb-1"
          style={{ scrollSnapType: 'x proximity', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth', scrollPaddingLeft: '24px', scrollPaddingRight: '24px', overflowY: 'visible' }}
          data-testid="career-timeline-scroller"
        >
          <ol
            className="relative inline-flex items-start gap-5 sm:gap-7"
            style={{ paddingTop: 4, paddingLeft: 16, paddingRight: 32 }}
          >
            {/* base hair-thin rail — `top` matches the vertical center
                of the dot/logo row below the year text. */}
            <span className="absolute left-0 right-0 top-[39px] h-px bg-white/8" aria-hidden />
            {/* progress sweep — smoother + slimmer than v1 */}
            <span
              className="absolute left-0 top-[39px] h-px transition-[width] duration-[1100ms] ease-out"
              style={{
                width: `${fillPct}%`,
                background: 'linear-gradient(90deg, rgba(107,184,240,0) 0%, rgba(107,184,240,0.9) 22%, rgba(180,222,255,1) 50%, rgba(107,184,240,0.9) 78%, rgba(107,184,240,0) 100%)',
                boxShadow: '0 0 12px rgba(107,184,240,0.45)',
              }}
              aria-hidden
            />
            {milestones.map((m, i) => {
              const revealed = i < revealedCount;
              const current = m.current || i === milestones.length - 1;
              const logo = m.image ? resolveLogo(m.image) : null;
              const stats = Array.isArray(m.stats) ? m.stats.filter(s => s && s.label && (s.value ?? '') !== '') : [];
              const hasStats = stats.length > 0;
              const isOpen = openIdx === i;
              return (
                <li
                  key={`${m.year}-${m.label}-${i}`}
                  className={`relative shrink-0 w-[88px] sm:w-[104px] text-center transition-all duration-500 ease-out ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1.5'}`}
                  style={{ scrollSnapAlign: 'start' }}
                  data-testid={`timeline-milestone-${i}`}
                >
                  <p className={`font-display text-[13px] sm:text-[14px] tracking-wider mb-2 leading-none ${current ? 'text-white' : 'text-white/80'}`}>
                    {m.year}
                  </p>
                  {/* The tap target — dot/logo. If this milestone has
                      stats attached, tapping toggles the liquid-glass
                      popover below. Otherwise it's a plain visual node
                      (no click). */}
                  <button
                    ref={(el) => { btnRefs.current[i] = el; }}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!hasStats) return;
                      if (isOpen) {
                        setOpenIdx(null);
                        setPopoverAnchor(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPopoverAnchor({ left: rect.left + rect.width / 2, top: rect.bottom });
                      setOpenIdx(i);
                    }}
                    aria-expanded={isOpen}
                    aria-label={hasStats ? `${m.year} stats` : `${m.year} ${m.label}`}
                    className={`relative inline-flex items-center justify-center bg-transparent border-0 p-0 ${hasStats ? 'cursor-pointer' : 'cursor-default'}`}
                    style={{ height: 22 }}
                    data-testid={`timeline-milestone-btn-${i}`}
                  >
                    {logo ? (
                      <img
                        src={logo}
                        alt=""
                        className={`relative z-[1] block w-[18px] h-[18px] rounded-full object-contain bg-black p-[1px] ring-1 transition-all duration-500 ${current || isOpen ? 'ring-blue-300/90' : 'ring-white/25'} ${hasStats ? 'hover:scale-110 hover:ring-blue-300/90' : ''}`}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling && (e.currentTarget.nextElementSibling.style.display = 'block'); }}
                      />
                    ) : null}
                    {/* dot fallback when no logo is set — also shown if
                        the logo URL 404s (the onError above unhides it). */}
                    <span
                      style={{ display: logo ? 'none' : 'block' }}
                      className={`relative z-[1] rounded-full transition-all duration-500 ${current || isOpen ? 'w-2.5 h-2.5 bg-white ring-[1.5px] ring-blue-300/80' : 'w-1.5 h-1.5 bg-white/90'} ${hasStats ? 'hover:scale-125' : ''}`}
                    />
                    {(current || isOpen) && (
                      <span className="absolute inline-flex w-6 h-6 rounded-full bg-blue-400/15 animate-ping" aria-hidden />
                    )}
                  </button>
                  <p className={`font-italic mt-2 text-[10px] leading-snug px-1 ${current ? 'text-blue-200' : 'text-gray-400/90'}`}>
                    {m.label}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
        {/* Smooth edge masks — wider than v1 (32px vs 24px) and using
            multi-stop gradients so the first/last nodes "fade into" the
            page instead of getting hard-clipped by the scroller edge. */}
        <span
          className="pointer-events-none absolute left-0 top-0 bottom-1 w-10"
          style={{ background: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0) 100%)' }}
          aria-hidden
        />
        <span
          className="pointer-events-none absolute right-0 top-0 bottom-1 w-10"
          style={{ background: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0) 100%)' }}
          aria-hidden
        />
      </div>

      {/* ── Liquid-glass Quick Stats popover ─────────────────────────
          Rendered at the section root with `position: fixed` so it
          escapes the horizontal scroller's clipping and any nested
          stacking contexts on the athlete page. Anchor coords come
          from the trigger button's getBoundingClientRect at click. */}
      {openIdx !== null && popoverAnchor && (() => {
        const m = milestones[openIdx];
        const stats = Array.isArray(m?.stats) ? m.stats.filter(s => s && s.label && (s.value ?? '') !== '') : [];
        if (!m || stats.length === 0) return null;
        // Clamp horizontally so the 240px popover never spills off-screen.
        const POP_W = 240;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const centerX = popoverAnchor.left;
        const half = POP_W / 2;
        const left = Math.max(12, Math.min(vw - POP_W - 12, centerX - half));
        return (
          <div
            role="dialog"
            aria-label={`${m.year} quick stats`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="fixed z-[100] w-[240px] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.85)] animate-in fade-in zoom-in-95 duration-200"
            style={{
              left: `${left}px`,
              top: `${popoverAnchor.top + 10}px`,
              background: '#000',
              border: '1px solid rgba(107,184,240,0.55)',
              boxShadow: '0 0 0 1px rgba(107,184,240,0.2) inset, 0 24px 70px rgba(0,0,0,0.85), 0 0 44px rgba(107,184,240,0.28)',
            }}
            data-testid={`timeline-milestone-popover-${openIdx}`}
          >
            {/* thin blue rail on top — echoes the timeline arc */}
            <div
              aria-hidden
              className="h-[2px] w-full"
              style={{
                background: 'linear-gradient(90deg, rgba(107,184,240,0) 0%, rgba(107,184,240,0.9) 50%, rgba(107,184,240,0) 100%)',
                boxShadow: '0 0 12px rgba(107,184,240,0.55)',
              }}
            />
            <div className="p-3 text-left">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <p className="font-display text-[11px] uppercase tracking-[0.22em] text-blue-300">{m.year}</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOpenIdx(null); setPopoverAnchor(null); }}
                  className="text-white/50 hover:text-white text-xs leading-none px-1"
                  aria-label="Close stats"
                  data-testid={`timeline-milestone-popover-close-${openIdx}`}
                >×</button>
              </div>
              <p className="text-[12px] italic text-white/85 leading-snug mb-3">{m.label}</p>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                {stats.map((s, k) => {
                  // Per-stat color set on the admin side (see MilestoneStatsEditor).
                  // Falls back to the brand green so unset stats still pop.
                  const c = s.color || '#22c55e';
                  return (
                    <li
                      key={`${s.label}-${k}`}
                      className="rounded-lg px-2 py-1.5"
                      style={{
                        background: '#000',
                        border: `1px solid ${c}66`,
                        boxShadow: `0 0 12px ${c}22, inset 0 0 0 1px ${c}22`,
                      }}
                    >
                      <p className="font-display text-[15px] leading-tight" style={{ color: c }}>{s.value}</p>
                      <p className="text-[9px] uppercase tracking-[0.15em] text-white/70 leading-tight mt-0.5 truncate">{s.label}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        );
      })()}
    </section>
  );
};

export default CareerTimeline;
